/**
 * DEFUSE S15 — typed property descriptors for the annotation kinds
 * (Dimension / Multileader / Revision Cloud). Special fields only.
 *
 * DERIVED point fields (empty `path`, computed accessor over a graph-backed
 * structure) whose WRITE-back reconciler is an S15 prerequisite:
 *   - Multileader Arrow/Landing → `leaderRoots[].nodes[].lines[].vertices`
 *     (the typed home the `__mleader_leader_roots` side-channel encodes today;
 *      `numericFieldClassification` explicitly leaves these unmapped as
 *      "nested-point entries that require dedicated parsing").
 *   - Revision Cloud Corner 1/2 → the AABB of `vertices` (the bag surfaces
 *     `corner1`/`corner2`; the typed home is the vertex chain).
 */

import type {
  DimensionEntity,
  MultileaderEntity,
  RevisionCloudEntity,
} from "@dxf-ai-editor/drawing-model";

import { formatNumber } from "../geometryEntityTypes";
import { dimensionSubtypeLabel } from "../previewBuilders";
import {
  bool,
  calc,
  coord,
  enumField,
  make,
  num,
  styleRef,
  txt,
  type TypedPropertyDescriptor,
} from "./types";

/* ── Dimension ─────────────────────────────────────────────────────────────── */

type Pt2 = { x: number; y: number } | undefined;

/** derived dimension-line coord with the `defPoint || textMidpoint` fallback
 *  (path "" — the shown value spans two fields; the write reconciler is S15's). */
function dimLine(
  label: string,
  def: (e: DimensionEntity) => Pt2,
  text: (e: DimensionEntity) => Pt2,
  axis: "x" | "y",
): TypedPropertyDescriptor {
  const val = (e: DimensionEntity) => (def(e)?.[axis] ?? 0) || (text(e)?.[axis] ?? 0);
  return make(label, "point-coord", { path: "", read: (e) => val(e as DimensionEntity) }, {
    constraints: { numeric: { precision: 3 } },
    display: (e) => formatNumber(val(e as DimensionEntity)),
  });
}

/** optional dimension point coord — production shows "" when the point is absent
 *  (`entity.thirdPoint ? formatNumber(...) : ""`), not "0". */
function optionalPoint(
  label: string,
  pt: (e: DimensionEntity) => Pt2,
  axis: "x" | "y",
): TypedPropertyDescriptor {
  return make(label, "point-coord", { path: "", read: (e) => pt(e as DimensionEntity)?.[axis] ?? 0 }, {
    constraints: { numeric: { precision: 3 } },
    display: (e) => {
      const p = pt(e as DimensionEntity);
      return p ? formatNumber(p[axis]) : "";
    },
  });
}

export const dimensionDescriptors: TypedPropertyDescriptor[] = [
  // decoded subtype label ("Linear") from the `dimensionType` low nibble — the
  // display mirrors dxfGeometry.buildDimensionPreview's dimensionSubtypeLabel
  // EXACTLY (raw bitfield exposed via read for reference).
  calc<DimensionEntity>(
    "Subtype",
    (e) => e.dimensionType,
    (e) => dimensionSubtypeLabel(e.dimensionType),
  ),
  styleRef<DimensionEntity>("Dimension style", "styleName", (e) => e.styleName),
  // production shows `dimensionText || "<>"` (the "<>" placeholder = "use the
  // measured value") — an empty override must display "<>", not "".
  make(
    "Text override",
    "text",
    { path: "dimensionText", read: (e) => (e as DimensionEntity).dimensionText },
    {
      display: (e) => (e as DimensionEntity).dimensionText || "<>",
      toRaw: (s) => (s === "<>" ? "" : s),
    },
  ),
  num<DimensionEntity>("Text height", "textHeight", (e) => e.textHeight),
  num<DimensionEntity>("Text rotation", "textRotation", (e) => e.textRotation),
  coord<DimensionEntity>("Extension 1 X", "extLine1Point.x", (e) => e.extLine1Point?.x ?? 0),
  coord<DimensionEntity>("Extension 1 Y", "extLine1Point.y", (e) => e.extLine1Point?.y ?? 0),
  coord<DimensionEntity>("Extension 2 X", "extLine2Point.x", (e) => e.extLine2Point?.x ?? 0),
  coord<DimensionEntity>("Extension 2 Y", "extLine2Point.y", (e) => e.extLine2Point?.y ?? 0),
  // DXF group 10 definition point = the dimension-line location. Production
  // falls back to textMidpoint when defPoint's component is 0/absent
  // (`formatNumber(defPt.x || textMid.x)`, defPt/textMid `?? {0,0}`) — an
  // imported zero-coordinate dim shows the textMidpoint value (P2 R6 #3). DERIVED
  // (path "") since the shown value spans two fields; the write reconciler
  // (which field a manual edit targets) is S15's, like Multileader Arrow.
  dimLine("Dimension line X", (e) => e.defPoint, (e) => e.textMidpoint, "x"),
  dimLine("Dimension line Y", (e) => e.defPoint, (e) => e.textMidpoint, "y"),
  // Third/Arc points are OPTIONAL — production shows "" when absent, not "0".
  optionalPoint("Third point X", (e) => e.thirdPoint, "x"),
  optionalPoint("Third point Y", (e) => e.thirdPoint, "y"),
  optionalPoint("Arc point X", (e) => e.arcPoint, "x"),
  optionalPoint("Arc point Y", (e) => e.arcPoint, "y"),
  num<DimensionEntity>("Oblique angle", "obliqueAngle", (e) => e.obliqueAngle),
  num<DimensionEntity>("Leader length", "leaderLength", (e) => e.leaderLength),
  // *Dnn anonymous block name — has a typed home but not user-editable.
  make(
    "Anonymous block",
    "text",
    { path: "anonymousBlockName", read: (e) => (e as DimensionEntity).anonymousBlockName },
    { constraints: { readOnly: true } },
  ),
  // formatted measurement (Appendix B pureUiKeys) — derived display.
  calc<DimensionEntity>("Measurement", (e) => e.measurement),
  num<DimensionEntity>("Angle", "linearAngle", (e) => e.linearAngle),
];

/* ── Multileader ───────────────────────────────────────────────────────────── */

function firstLeaderVertex(e: MultileaderEntity): { x: number; y: number } | undefined {
  return e.leaderRoots[0]?.nodes[0]?.lines[0]?.vertices[0];
}

/** total leader LINES across every root → node → line (P2 #5 — NOT roots.length,
 *  which undercounts multi-node / multi-line multileaders). */
function leaderLineCount(e: MultileaderEntity): number {
  return e.leaderRoots.reduce(
    (sum, root) => sum + root.nodes.reduce((s, node) => s + node.lines.length, 0),
    0,
  );
}

/** derived point-coord (write-back reconciler = S15 prerequisite). Production
 *  emits Arrow/Landing via `String(...)` (NOT formatNumber), so fractional
 *  leader vertices / text-locations keep their FULL stored precision in the bag
 *  — the display must reproduce that exactly (P2 R6 #4). */
function derivedCoord(
  label: string,
  read: (e: MultileaderEntity) => number,
): TypedPropertyDescriptor {
  return make(label, "point-coord", { path: "", read: (e) => read(e as MultileaderEntity) }, {
    constraints: { numeric: { precision: 3 } },
    display: (e) => String(read(e as MultileaderEntity)),
  });
}

export const multileaderDescriptors: TypedPropertyDescriptor[] = [
  styleRef<MultileaderEntity>("Multileader style", "styleName", (e) => e.styleName),
  txt<MultileaderEntity>("Contents", "textContents", (e) => e.textContents),
  num<MultileaderEntity>("Text height", "textHeight", (e) => e.textHeight),
  // Arrow = first leader-line first vertex, DEFAULTING TO 0 for a valid
  // leaderless (content-only) multileader — leaderRoots can be [] (P2 R4 #4;
  // matches buildMultileaderPreview's `firstVertex ? … : "0"`).
  derivedCoord("Arrow X", (e) => firstLeaderVertex(e)?.x ?? 0),
  derivedCoord("Arrow Y", (e) => firstLeaderVertex(e)?.y ?? 0),
  // Landing = textLocation (P2 #4 — the production preview projects Landing X/Y
  // from entity.textLocation, NOT the leader-line endpoint).
  derivedCoord("Landing X", (e) => e.textLocation?.x ?? 0),
  derivedCoord("Landing Y", (e) => e.textLocation?.y ?? 0),
  num<MultileaderEntity>("Arrowhead size", "arrowheadSize", (e) => e.arrowheadSize),
  txt<MultileaderEntity>("Arrowhead block", "arrowheadBlock", (e) => e.arrowheadBlock),
  enumField<MultileaderEntity>("Text attachment", "textAttachmentType", (e) => e.textAttachmentType),
  enumField<MultileaderEntity>(
    "Text direction",
    "textAttachmentDirection",
    (e) => e.textAttachmentDirection,
  ),
  bool<MultileaderEntity>("Text frame", "textFrameDisplay", (e) => e.textFrameDisplay),
  enumField<MultileaderEntity>(
    "Line spacing style",
    "textLineSpacingStyle",
    (e) => e.textLineSpacingStyle,
  ),
  num<MultileaderEntity>(
    "Line spacing factor",
    "textLineSpacingFactor",
    (e) => e.textLineSpacingFactor,
  ),
  txt<MultileaderEntity>("Leader color", "leaderLineColor", (e) => e.leaderLineColor),
  make("Leader weight", "lineweight", {
    path: "leaderLineWeight",
    read: (e) => (e as MultileaderEntity).leaderLineWeight,
  }),
  enumField<MultileaderEntity>("Leader type", "leaderLineType", (e) => e.leaderLineType),
  txt<MultileaderEntity>("Block name", "blockName", (e) => e.blockName),
  num<MultileaderEntity>("Block scale", "blockScale", (e) => e.blockScale),
  num<MultileaderEntity>("Block rotation", "blockRotation", (e) => e.blockRotation),
  num<MultileaderEntity>("Override flags", "propertyOverrideFlags", (e) => e.propertyOverrideFlags),
  num<MultileaderEntity>("Landing gap", "landingGap", (e) => e.landingGap),
  calc<MultileaderEntity>("Leader count", leaderLineCount),
];

/* ── Revision Cloud ────────────────────────────────────────────────────────── */

function bounds(e: RevisionCloudEntity): { minX: number; minY: number; maxX: number; maxY: number } {
  const xs = e.vertices.map((v) => v.x);
  const ys = e.vertices.map((v) => v.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function derivedCorner(
  label: string,
  read: (b: ReturnType<typeof bounds>) => number,
): TypedPropertyDescriptor {
  return make(label, "point-coord", { path: "", read: (e) => read(bounds(e as RevisionCloudEntity)) }, {
    constraints: { numeric: { precision: 3 } },
  });
}

export const revisionCloudDescriptors: TypedPropertyDescriptor[] = [
  styleRef<RevisionCloudEntity>("Cloud style", "revisionCloudStyle", (e) => e.revisionCloudStyle),
  // REVCLOUD-PROPS-EDITOR (§5.1) — the arc-length pair AutoCAD's REVCLOUD
  // "Arc length" option / Properties palette exposes. `arcChordLength` (DXF group
  // for the traced chord) is the MINIMUM bump chord; `approximateArcLength` is the
  // nominal/approximate value that DRIVES the rendered bump count
  // (`buildRevisionCloudSceneGeometry`: bulges = round(segLen / approxArcLength)),
  // so editing it visibly re-tessellates the cloud each frame — the "regen arcs"
  // path. Both persist through `properties/apply` → the legacy translator
  // (`labelToFieldByType["Revision Cloud"]`, numericFieldClassification). Positive-
  // finite — the S16 widget + form validation reject ≤ 0 / non-finite.
  num<RevisionCloudEntity>(
    "Minimum arc length",
    "arcChordLength",
    (e) => e.arcChordLength,
    { exclusiveMin: 0 },
  ),
  num<RevisionCloudEntity>(
    "Approximate arc length",
    "approximateArcLength",
    (e) => e.approximateArcLength,
    { exclusiveMin: 0 },
  ),
  // creation-method row emitted directly into the bag by the preview builder
  // (dxfGeometry / deriveRevisionCloudPreviewProperties). Kept a read-only-calc
  // DESCRIPTOR (the plain Properties grid shows it read-only), but REVCLOUD-PROPS-
  // EDITOR surfaces it as an editable select in its bespoke form — the form reads
  // the value via this descriptor's accessor and writes back through
  // `properties/apply` ("Creation mode" → `creationMode`, labelToFieldByType), so
  // no channel flip is needed and the enum stays off the typed-writer's raw-int
  // enum path (ПРИОРИТЕТ #1).
  calc<RevisionCloudEntity>("Creation mode", (e) => e.creationMode),
  // corner1/corner2 in the bag ⇒ AABB of the typed vertex chain.
  derivedCorner("Corner 1 X", (b) => b.minX),
  derivedCorner("Corner 1 Y", (b) => b.minY),
  derivedCorner("Corner 2 X", (b) => b.maxX),
  derivedCorner("Corner 2 Y", (b) => b.maxY),
  // production emits Closed as "true"/"false" (revcloud is stored as a closed
  // LWPOLYLINE) — same true/false form as LWPOLYLINE.
  make(
    "Closed",
    "boolean",
    { path: "closed", read: (e) => (e as RevisionCloudEntity).closed },
    {
      display: (e) => ((e as RevisionCloudEntity).closed ? "true" : "false"),
      toRaw: (s) => s.trim().toLowerCase() === "true",
    },
  ),
];
